import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2, Star } from 'lucide-react';
import { getServices } from '../api/client';

const formatPrice = (from, to) => {
  const fmt = (n) => n >= 1000000
    ? `Rp ${(n/1000000).toFixed(n%1000000===0?0:1)} Jt`
    : `Rp ${(n/1000).toFixed(0)} Rb`;
  if (!from && !to) return 'Hubungi Kami';
  if (!to || to === 0) return `Mulai ${fmt(from)}`;
  return `${fmt(from)} – ${fmt(to)}`;
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef();

  useEffect(() => {
    getServices().then(d => { setServices(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { e.target.classList.add('visible'); obs.disconnect(); }
    }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const nav = () => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });

  if (!loading && services.length === 0) return null;

  return (
    <div style={{ padding: '120px 0', background: 'var(--surface)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 32px' }}>
        <div ref={ref} className='reveal' style={{ marginBottom: 64, position: 'relative' }}>
          <div className='ghost-number' style={{ position: 'absolute', left: -20, top: -60, zIndex: 0 }}>SV</div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#6a6a88', letterSpacing: 3, marginBottom: 12 }}>LAYANAN</div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(36px,6vw,72px)', fontWeight: 700, color: '#f2f2ff', margin: 0, letterSpacing: '-2px', lineHeight: 1 }}>
              Apa yang Bisa<br />
              <span style={{ background: 'linear-gradient(135deg,#00e5ff,#9b00ff,#ff0099,#ffb700,#00e5ff)', backgroundSize: '400% 400%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'holo-shift 5s ease infinite' }}>Saya Bantu?</span>
            </h2>
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, color: '#6a6a88', gap: 10, fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> MEMUAT...
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }} className='port-grid'>
            {services.map((svc, i) => (
              <div key={svc.id}
                style={{
                  background: 'var(--card)',
                  border: '1px solid',
                  borderColor: svc.is_featured ? 'transparent' : 'rgba(255,255,255,0.07)',
                  backgroundImage: svc.is_featured
                    ? 'linear-gradient(var(--card),var(--card)) padding-box, linear-gradient(135deg,#00e5ff,#9b00ff,#ff0099) border-box'
                    : 'none',
                  borderRadius: 4, padding: '28px 24px',
                  position: 'relative', overflow: 'hidden',
                  animation: `fadeUp 0.6s ease ${i*0.1}s both`,
                  transition: 'transform 0.25s, box-shadow 0.25s',
                  cursor: 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {svc.is_featured && (
                  <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 2, background: 'linear-gradient(135deg,#00e5ff,#9b00ff)', fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#07070f', fontWeight: 700, letterSpacing: 1 }}>
                    <Star size={9} fill='#07070f' /> POPULER
                  </div>
                )}

                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 18, color: '#f2f2ff', marginBottom: 10, letterSpacing: '-0.3px' }}>{svc.name}</div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: '#6a6a88', lineHeight: 1.7, marginBottom: 20 }}>{svc.description}</div>

                {/* Price */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 22, color: '#f2f2ff', letterSpacing: '-0.5px' }}>
                    <span style={{ background: 'linear-gradient(135deg,#00e5ff,#9b00ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                      {formatPrice(svc.price_from, svc.price_to)}
                    </span>
                  </div>
                  {svc.unit && <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#6a6a88', letterSpacing: 1, marginTop: 2 }}>/{svc.unit.toUpperCase()}</div>}
                </div>

                {/* Features */}
                {(svc.features || []).length > 0 && (
                  <ul style={{ listStyle: 'none', margin: '0 0 24px', padding: 0 }}>
                    {svc.features.map((f, fi) => (
                      <li key={fi} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                        <CheckCircle2 size={13} color='#00e5ff' style={{ marginTop: 2, flexShrink: 0 }} />
                        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: '#9090a8' }}>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <button onClick={nav} style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '11px', borderRadius: 3, border: 'none', cursor: 'none',
                  background: svc.is_featured ? 'linear-gradient(135deg,#00e5ff,#9b00ff)' : 'rgba(255,255,255,0.05)',
                  color: svc.is_featured ? '#07070f' : '#f2f2ff',
                  fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1,
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  HUBUNGI SEKARANG <ArrowRight size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
